import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, GraduationCap, Trash2, Calendar, Check, Clock,
  BookOpen, CheckCheck, Filter, ExternalLink, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  offen:    { label: "Offen",    color: "bg-sand-100 text-sand-800 border-sand-200",     icon: Clock },
  geplant:  { label: "Geplant",  color: "bg-amber-50 text-amber-900 border-amber-200",   icon: Calendar },
  gebucht:  { label: "Gebucht",  color: "bg-blue-50 text-blue-800 border-blue-200",      icon: BookOpen },
  erledigt: { label: "Erledigt", color: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCheck },
};
const STATUS_ORDER = ["offen", "geplant", "gebucht", "erledigt"];

const PRIO_META = {
  hoch:    { color: "bg-red-100 text-red-800 border-red-200", label: "hoch" },
  mittel:  { color: "bg-amber-100 text-amber-900 border-amber-200", label: "mittel" },
  niedrig: { color: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "niedrig" },
};

export default function Fortbildungsplan() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | offen | geplant | gebucht | erledigt
  const [editItem, setEditItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/fortbildungsplan`, { withCredentials: true });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch {
      toast.error("Plan konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((it) => it.status === filter);
  }, [items, filter]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/fortbildungsplan/${id}`, { status }, { withCredentials: true });
      toast.success(`Status: ${STATUS_META[status]?.label || status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktualisierung fehlgeschlagen.");
    }
  };

  const remove = async (id) => {
    if (!confirm("Diesen Eintrag wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/fortbildungsplan/${id}`, { withCredentials: true });
      toast.success("Gelöscht");
      load();
    } catch (e) {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const totalOpen = (counts.offen || 0) + (counts.geplant || 0);

  return (
    <div data-testid="fortbildungsplan-page" className="min-h-screen bg-cream-50 pb-20">
      <div className="border-b border-cream-300 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <Link
            to="/campus/app"
            className="inline-flex items-center gap-1.5 text-[13px] text-sand-600 hover:text-brand-darkred mb-3"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Zurück zum Dashboard
          </Link>
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center rounded-full bg-brand-darkred text-cream-50 text-[10px] font-bold tracking-widest px-2 py-0.5">
              TEAM-BACKLOG
            </span>
            <span className="text-[11px] text-sand-500">
              aus KI-MDK-Simulationen
            </span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-sand-800 leading-none flex items-center gap-2">
            <GraduationCap className="h-7 w-7 text-brand-darkred" strokeWidth={1.6} />
            Fortbildungsplan
          </h1>
          <p className="text-[14px] text-sand-600 mt-2 max-w-2xl leading-relaxed">
            Alle Fortbildungs-Empfehlungen, die du aus MD-Prüfsimulationen übernommen hast —
            gebündelt in einem Team-Backlog. Setze Termine, markiere gebuchte Kurse und
            hake ab, was erledigt ist.
          </p>
          <div className="flex flex-wrap gap-2 mt-4 text-[12px]">
            <FilterChip
              active={filter === "all"}
              label="Alle"
              count={items.length}
              onClick={() => setFilter("all")}
            />
            {STATUS_ORDER.map((s) => (
              <FilterChip
                key={s}
                active={filter === s}
                label={STATUS_META[s].label}
                count={counts[s] || 0}
                onClick={() => setFilter(s)}
                variant={s}
              />
            ))}
            {totalOpen > 0 && (
              <span className="ml-auto inline-flex items-center gap-1 text-[11.5px] text-sand-500 self-center">
                {totalOpen} offene Empfehlungen
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {loading && (
          <div className="flex items-center justify-center py-20 text-sand-600 text-[14px]">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade Plan …
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="rounded-3xl bg-white border border-cream-300 p-8 sm:p-12 text-center">
            <GraduationCap className="h-10 w-10 text-sand-400 mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-display text-2xl text-sand-800 mb-2">
              {filter === "all" ? "Noch keine Fortbildungen im Plan" : `Keine Einträge mit Status "${STATUS_META[filter]?.label}"`}
            </h3>
            <p className="text-[14px] text-sand-600 max-w-md mx-auto">
              Öffne eine Patientenmappe, starte die <strong className="text-brand-darkred">MD-Prüfsimulation</strong> und
              klicke bei jeder Empfehlung auf „In Fortbildungsplan".
            </p>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="space-y-2">
            {filtered.map((it) => {
              const statusMeta = STATUS_META[it.status] || STATUS_META.offen;
              const prio = PRIO_META[it.prioritaet] || PRIO_META.mittel;
              const StatusIcon = statusMeta.icon;
              return (
                <div
                  key={it.id}
                  data-testid={`plan-item-${it.id}`}
                  data-status={it.status}
                  className="rounded-2xl bg-white border border-cream-300 p-4 sm:p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 mb-1">
                        <span className={`inline-flex items-center gap-1 rounded-full border ${statusMeta.color} text-[10.5px] font-bold uppercase tracking-widest px-2 py-0.5`}>
                          <StatusIcon className="h-3 w-3" /> {statusMeta.label}
                        </span>
                        <span className={`inline-flex items-center rounded-full border ${prio.color} text-[10.5px] font-bold uppercase tracking-widest px-2 py-0.5`}>
                          {prio.label}
                        </span>
                        {it.mappe_label && (
                          <Link
                            to={`/campus/app/patientenmappe/${it.mappe_id}`}
                            className="inline-flex items-center gap-0.5 text-[11px] text-sand-500 hover:text-brand-darkred"
                          >
                            aus „{it.mappe_label}" <ExternalLink className="h-2.5 w-2.5" />
                          </Link>
                        )}
                      </div>
                      <div className="font-display text-lg sm:text-xl text-sand-800 leading-tight">{it.titel}</div>
                      {it.zielgruppe && (
                        <div className="text-[12px] text-sand-500 mt-0.5">
                          Zielgruppe: <span className="font-semibold text-sand-700">{it.zielgruppe}</span>
                        </div>
                      )}
                      {it.begruendung && (
                        <p className="text-[13px] text-sand-700 leading-relaxed mt-1.5">{it.begruendung}</p>
                      )}
                      {(it.notiz || it.termin) && (
                        <div className="mt-2 rounded-lg bg-cream-50 border border-cream-200 px-3 py-2 text-[12.5px] text-sand-700">
                          {it.termin && (
                            <div className="flex items-center gap-1.5">
                              <Calendar className="h-3 w-3 text-brand-darkred" />
                              <span className="font-semibold">Termin:</span> {it.termin}
                            </div>
                          )}
                          {it.notiz && <div className="mt-0.5"><span className="font-semibold">Notiz:</span> {it.notiz}</div>}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <button
                        onClick={() => setEditItem(it)}
                        data-testid={`btn-plan-edit-${it.id}`}
                        title="Bearbeiten"
                        className="p-1.5 rounded-full text-sand-500 hover:text-brand-darkred hover:bg-cream-100"
                      >
                        <Calendar className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => remove(it.id)}
                        data-testid={`btn-plan-delete-${it.id}`}
                        title="Löschen"
                        className="p-1.5 rounded-full text-sand-400 hover:text-brand-darkred hover:bg-cream-100"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Status-Buttons */}
                  <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-cream-200">
                    {STATUS_ORDER.map((s) => (
                      <button
                        key={s}
                        onClick={() => updateStatus(it.id, s)}
                        disabled={it.status === s}
                        data-testid={`btn-plan-status-${it.id}-${s}`}
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                          it.status === s
                            ? "bg-brand-darkred text-cream-50 cursor-default"
                            : "bg-cream-50 border border-cream-300 text-sand-700 hover:border-brand-darkred/40"
                        }`}
                      >
                        {it.status === s && <Check className="h-3 w-3" />}
                        {STATUS_META[s].label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editItem && (
        <EditPlanModal
          item={editItem}
          onClose={() => setEditItem(null)}
          onSaved={() => { setEditItem(null); load(); }}
        />
      )}
    </div>
  );
}

function FilterChip({ active, label, count, onClick, variant }) {
  const activeCls = variant
    ? (STATUS_META[variant]?.color || "bg-brand-darkred text-cream-50 border-brand-darkred")
    : "bg-brand-darkred text-cream-50 border-brand-darkred";
  return (
    <button
      onClick={onClick}
      data-testid={`filter-${variant || "all"}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors ${
        active ? activeCls : "bg-white border-cream-300 text-sand-700 hover:border-brand-darkred/40"
      }`}
    >
      {label}
      <span className="tabular-nums text-[10.5px] opacity-80">({count})</span>
    </button>
  );
}

function EditPlanModal({ item, onClose, onSaved }) {
  const [termin, setTermin] = useState(item.termin || "");
  const [notiz, setNotiz] = useState(item.notiz || "");
  const [prio, setPrio] = useState(item.prioritaet || "mittel");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/fortbildungsplan/${item.id}`,
        { termin, notiz, prioritaet: prio },
        { withCredentials: true },
      );
      toast.success("Gespeichert");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-espresso-deep/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-hover max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-cream-300">
          <h3 className="font-display text-xl text-sand-800">Bearbeiten</h3>
          <button onClick={onClose} className="p-1 rounded-full text-sand-400 hover:text-sand-800" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-[13px] text-sand-800 font-semibold">{item.titel}</div>
          <label className="block">
            <span className="block text-[11.5px] font-semibold uppercase tracking-wider text-sand-600 mb-1">Termin</span>
            <input
              type="date"
              value={termin}
              onChange={(e) => setTermin(e.target.value)}
              data-testid="edit-termin"
              className="w-full rounded-lg border border-cream-300 bg-white px-3 py-2 text-[14px] text-sand-800 focus:outline-none focus:border-brand-darkred/60"
            />
          </label>
          <label className="block">
            <span className="block text-[11.5px] font-semibold uppercase tracking-wider text-sand-600 mb-1">Priorität</span>
            <select
              value={prio}
              onChange={(e) => setPrio(e.target.value)}
              data-testid="edit-prio"
              className="w-full rounded-lg border border-cream-300 bg-white px-3 py-2 text-[14px] text-sand-800 focus:outline-none focus:border-brand-darkred/60"
            >
              <option value="hoch">hoch</option>
              <option value="mittel">mittel</option>
              <option value="niedrig">niedrig</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-[11.5px] font-semibold uppercase tracking-wider text-sand-600 mb-1">Notiz (optional)</span>
            <textarea
              value={notiz}
              onChange={(e) => setNotiz(e.target.value)}
              rows={4}
              data-testid="edit-notiz"
              placeholder="Bei welcher Akademie gebucht, Kosten, wer nimmt teil, …"
              className="w-full rounded-lg border border-cream-300 bg-white px-3 py-2 text-[13.5px] text-sand-800 focus:outline-none focus:border-brand-darkred/60 leading-relaxed"
            />
          </label>
          <div className="flex gap-2 pt-2">
            <button
              onClick={onClose}
              className="flex-1 rounded-full border border-cream-300 py-2.5 text-[13.5px] font-semibold text-sand-700"
            >
              Abbrechen
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="btn-edit-save"
              className="flex-1 rounded-full bg-brand-darkred text-cream-50 py-2.5 text-[13.5px] font-semibold hover:bg-brand-red disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Speichern
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
